import React, { useState, useEffect } from 'react';
import { uploadMenuImages, deleteMenuImage } from '../../../utils/storage';
import './AddEditItemModal.css';

const AddEditItemModal = ({ item, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    cookingTime: '',
    tags: [],
    isAvailable: true,
    isFeatured: false,
    images: [],
    customizations: {
      sizes: [],
      spiceLevels: [],
      addons: []
    }
  });
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (item) {
      // Filter out any blob URLs (temporary URLs that shouldn't be saved)
      // Also handle both array and single image cases
      let imageArray = item.images || [];
      if (!Array.isArray(imageArray)) {
        imageArray = imageArray ? [imageArray] : [];
      }
      
      const validImages = imageArray.filter(img => 
        img && typeof img === 'string' && !img.startsWith('blob:') && img.trim() !== ''
      );

      console.log('Loading item for edit:', {
        itemImages: item.images,
        validImages: validImages
      });

      setFormData({
        name: item.name || '',
        category: item.category || '',
        description: item.description || '',
        price: item.price || '',
        cookingTime: item.cookingTime || '',
        tags: Array.isArray(item.tags) ? item.tags : [],
        isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
        isFeatured: item.isFeatured || false,
        images: validImages,
        customizations: {
          sizes: Array.isArray(item.customizations?.sizes) ? item.customizations.sizes : [],
          spiceLevels: Array.isArray(item.customizations?.spiceLevels) ? item.customizations.spiceLevels : [],
          addons: Array.isArray(item.customizations?.addons) ? item.customizations.addons : []
        }
      });
      setCharCount(item.description?.length || 0);
    } else {
      // Reset form when not editing
      setFormData({
        name: '',
        category: '',
        description: '',
        price: '',
        cookingTime: '',
        tags: [],
        isAvailable: true,
        isFeatured: false,
        images: [],
        customizations: {
          sizes: [],
          spiceLevels: [],
          addons: []
        }
      });
      setCharCount(0);
      setUploadError('');
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'description') {
      setCharCount(value.length);
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => {
      const currentTags = Array.isArray(prev.tags) ? prev.tags : [];
      return {
        ...prev,
        tags: currentTags.includes(tag)
          ? currentTags.filter(t => t !== tag)
          : [...currentTags, tag]
      };
    });
  };

  const handleAddSize = () => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: [...prev.customizations.sizes, { name: '', price: '' }]
      }
    }));
  };

  const handleRemoveSize = (index) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: prev.customizations.sizes.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSizeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: prev.customizations.sizes.map((size, i) =>
          i === index ? { ...size, [field]: value } : size
        )
      }
    }));
  };

  const handleAddAddon = () => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: [...prev.customizations.addons, { name: '', price: '' }]
      }
    }));
  };

  const handleRemoveAddon = (index) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: prev.customizations.addons.filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddonChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: prev.customizations.addons.map((addon, i) =>
          i === index ? { ...addon, [field]: value } : addon
        )
      }
    }));
  };

  const handleSpiceLevelToggle = (level) => {
    setFormData(prev => {
      const currentSpiceLevels = Array.isArray(prev.customizations?.spiceLevels) 
        ? prev.customizations.spiceLevels 
        : [];
      return {
        ...prev,
        customizations: {
          ...prev.customizations,
          spiceLevels: currentSpiceLevels.includes(level)
            ? currentSpiceLevels.filter(l => l !== level)
            : [...currentSpiceLevels, level]
        }
      };
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }

    setUploadingImages(true);
    setUploadError('');

    try {
      // Upload images to Supabase Storage
      const uploadedUrls = await uploadMenuImages(files);
      
      // Add uploaded URLs to form data
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setUploadError(error.message || 'Failed to upload images. Please try again.');
      alert(error.message || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (index) => {
    const imageToRemove = formData.images[index];
    
    // If it's a Supabase URL (not a blob URL), delete it from storage
    if (imageToRemove && !imageToRemove.startsWith('blob:')) {
      try {
        await deleteMenuImage(imageToRemove);
      } catch (error) {
        console.error('Error deleting image from storage:', error);
        // Continue with removal from form even if storage deletion fails
      }
    } else if (imageToRemove && imageToRemove.startsWith('blob:')) {
      // Revoke blob URL to free memory
      URL.revokeObjectURL(imageToRemove);
    }

    // Remove from form data
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    // Check if there are any images
    if (formData.images.length === 0) {
      setErrors(prev => ({ ...prev, images: 'At least one image is required' }));
      return;
    }

    // If images are still uploading, wait
    if (uploadingImages) {
      alert('Please wait for images to finish uploading.');
      return;
    }

    // Filter out any invalid images (blob URLs, empty strings, etc.)
    const validImages = formData.images.filter(img => 
      img && 
      typeof img === 'string' && 
      !img.startsWith('blob:') && 
      img.trim() !== '' &&
      (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:'))
    );

    if (validImages.length === 0) {
      setErrors(prev => ({ ...prev, images: 'At least one valid image is required' }));
      return;
    }

    console.log('Saving item with images:', {
      totalImages: formData.images.length,
      validImages: validImages.length,
      images: validImages
    });

    // Prepare data for saving
    const dataToSave = {
      ...formData,
      price: parseFloat(formData.price),
      cookingTime: formData.cookingTime ? parseInt(formData.cookingTime) : null,
      // Use only valid image URLs
      images: validImages
    };

    // Call onSave callback
    onSave(dataToSave);
  };

  const availableTags = ['🌶️ Spicy', '⭐ Popular', '🥬 Veg', '🆕 New', '🏆 Best Seller'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{item ? 'Edit Item' : 'Add New Item'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-columns">
            <div className="form-left">
              <div className="form-group">
                <label className="form-label">
                  Item Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="Enter item name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Category <span className="required">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`form-input ${errors.category ? 'error' : ''}`}
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {errors.category && <span className="error-message">{errors.category}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`form-textarea ${errors.description ? 'error' : ''}`}
                  placeholder="Describe your item..."
                  rows="5"
                />
                <div className="char-counter">
                  {charCount}/500
                </div>
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Price (PKR) <span className="required">*</span>
                  </label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">PKR</span>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className={`form-input ${errors.price ? 'error' : ''}`}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {errors.price && <span className="error-message">{errors.price}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Cooking Time (minutes)</label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="cookingTime"
                      value={formData.cookingTime}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="0"
                      min="0"
                    />
                    <span className="input-suffix">mins</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tags</label>
                <div className="tags-checkboxes">
                  {availableTags.map(tag => (
                    <label key={tag} className="tag-checkbox">
                      <input
                        type="checkbox"
                        checked={Array.isArray(formData.tags) && formData.tags.includes(tag)}
                        onChange={() => handleTagToggle(tag)}
                      />
                      <span className="checkmark"></span>
                      {tag}
                    </label>
                  ))}
                </div>
              </div>

              <div className="customizations-section">
                <h3 className="section-title">Customization Options</h3>

                <div className="customization-group">
                  <div className="group-header">
                    <label className="group-label">Size Options</label>
                    <button type="button" className="add-option-btn" onClick={handleAddSize}>
                      + Add Size
                    </button>
                  </div>
                  {formData.customizations.sizes.map((size, index) => (
                    <div key={index} className="option-row">
                      <input
                        type="text"
                        placeholder="Size name"
                        value={size.name}
                        onChange={(e) => handleSizeChange(index, 'name', e.target.value)}
                        className="option-input"
                      />
                      <div className="input-with-prefix">
                        <span className="input-prefix">PKR</span>
                        <input
                          type="number"
                          placeholder="Price"
                          value={size.price}
                          onChange={(e) => handleSizeChange(index, 'price', e.target.value)}
                          className="option-input"
                        />
                      </div>
                      <button
                        type="button"
                        className="remove-option-btn"
                        onClick={() => handleRemoveSize(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <div className="customization-group">
                  <label className="group-label">Spice Levels</label>
                  <div className="spice-levels">
                    {['Mild', 'Medium', 'Spicy', 'Extra Spicy'].map(level => (
                      <label key={level} className="tag-checkbox">
                        <input
                          type="checkbox"
                          checked={Array.isArray(formData.customizations?.spiceLevels) && formData.customizations.spiceLevels.includes(level)}
                          onChange={() => handleSpiceLevelToggle(level)}
                        />
                        <span className="checkmark"></span>
                        {level}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="customization-group">
                  <div className="group-header">
                    <label className="group-label">Add-ons</label>
                    <button type="button" className="add-option-btn" onClick={handleAddAddon}>
                      + Add Add-on
                    </button>
                  </div>
                  {formData.customizations.addons.map((addon, index) => (
                    <div key={index} className="option-row">
                      <input
                        type="text"
                        placeholder="Add-on name"
                        value={addon.name}
                        onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                        className="option-input"
                      />
                      <div className="input-with-prefix">
                        <span className="input-prefix">PKR</span>
                        <input
                          type="number"
                          placeholder="Price"
                          value={addon.price}
                          onChange={(e) => handleAddonChange(index, 'price', e.target.value)}
                          className="option-input"
                        />
                      </div>
                      <button
                        type="button"
                        className="remove-option-btn"
                        onClick={() => handleRemoveAddon(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="form-right">
              <div className="form-group">
                <label className="form-label">
                  Item Photos <span className="required">*</span>
                </label>
                <div 
                  className={`upload-area ${uploadingImages ? 'uploading' : ''}`} 
                  onClick={() => !uploadingImages && document.getElementById('image-upload').click()}
                  style={{ cursor: uploadingImages ? 'wait' : 'pointer', opacity: uploadingImages ? 0.6 : 1 }}
                >
                  <input
                    id="image-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    style={{ display: 'none' }}
                  />
                  <span className="upload-icon">📷</span>
                  <p className="upload-text">
                    {uploadingImages ? 'Uploading images...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="upload-subtext">Max 5 photos, up to 5MB each</p>
                </div>
                {uploadError && (
                  <span className="error-message" style={{ display: 'block', marginTop: '8px' }}>
                    {uploadError}
                  </span>
                )}
                {errors.images && (
                  <span className="error-message" style={{ display: 'block', marginTop: '8px' }}>
                    {errors.images}
                  </span>
                )}
                {formData.images.length > 0 && (
                  <div className="image-previews">
                    {formData.images.map((img, index) => (
                      <div key={index} className="image-preview">
                        <img 
                          src={img} 
                          alt={`Preview ${index + 1}`}
                          onError={(e) => {
                            console.error('Error loading image:', img);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">Image failed to load</div>';
                          }}
                          onLoad={() => {
                            console.log('Image loaded successfully:', img);
                          }}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => handleRemoveImage(index)}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Availability</label>
                <div className="toggle-wrapper">
                  <label className="toggle-switch-large">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={formData.isAvailable}
                      onChange={handleChange}
                    />
                    <span className="toggle-slider-large"></span>
                  </label>
                  <span className="toggle-label">
                    {formData.isAvailable ? 'Available' : 'Sold Out'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Feature on Homepage</label>
                <label className="checkbox-label-large">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleChange}
                  />
                  <span className="checkmark-large"></span>
                  Show this item on homepage
                </label>
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn">
              Save Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditItemModal;

